import { Box } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { RuleBuilder, RuleBuilderProvider } from '@procureai/rule-builder-ui-mui';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';

import { RuleDomainType } from '~/common/types';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon.tsx';
import { LoaderOverlay } from '~/components/LoaderOverlay/LoaderOverlay';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { RestrictedRoute } from '~/components/RestrictedRoute/RestrictedRoute';
import { useRuleDetail } from '~/modules';
import { RuleDetailPageTitle } from '~/routes/RuleDetail/components/RuleDetailPageTitle/RuleDetailPageTitle.tsx';
import { RulePageActions } from '~/routes/RuleDetail/components/RulePageActions/RulePageActions.tsx';

import { RulePublishAlert } from './components/RulePublishAlert/RulePublishAlert';
import { RuleVersionActionsProvider } from './components/RuleVersionActionsProvider/RuleVersionActionsProvider';
import { useRuleBuilderBackend } from './hooks/useRuleBuilderBackend/useRuleBuilderBackend';

const InnerRuleDetail = ({ id }: { id: string }) => {
  const { data, variant, stats, saving, loading, onSubmit, onValidationError } = useRuleBuilderBackend({ id });

  const isProcessing = saving || loading;
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
      }}
    >
      {isProcessing ? <LoaderOverlay active /> : null}
      {data ? (
        <RuleBuilder
          variant={variant}
          data={data}
          stats={stats}
          onSubmit={onSubmit}
          onValidationError={onValidationError}
        />
      ) : null}
      <RulePublishAlert />
    </Box>
  );
};

export const RuleDetail = () => {
  const { ruleId: id } = useRuleDetail();
  return (
    <RuleBuilderProvider>
      <RuleVersionActionsProvider id={id}>
        <PageLayout
          disablePadding
          fullHeight
          renderHeader={
            <PageHeaderStatic
              title={<RuleDetailPageTitle id={id} />}
              maxWidth={false}
              icon={<WidgetIcon icon={<DomainIcon domain={RuleDomainType.RULE_SOURCING} />} />}
              right={<RulePageActions id={id} />}
            />
          }
        >
          <RestrictedRoute needs={{ permission: Permission.RuleEdit }}>
            <InnerRuleDetail id={id} />
          </RestrictedRoute>
        </PageLayout>
      </RuleVersionActionsProvider>
    </RuleBuilderProvider>
  );
};
